// Preliminary sizing of a one-way spanning X-LAM (CLT) floor, 1 m strip, simply supported, uniform load.
// Gamma method with rolling shear of the transverse layers; CL24 values supplied by Emma Industrie.
import DATA from "@/data/lamellare.json";
import { CODES } from "./beamCalc";

const CL24 = { fm: 24, fr: 0.7, E: 11000, Gr: 65 };
const KMOD = 0.8;
const KDEF = { 1: 0.8, 2: 1.0 };
const RHO = 4.2; // kN/m3 (ρmean 420 kg/m³)

const parse = (comp) => comp.split("-").map((s) => ({ t: parseFloat(s), dir: s.slice(-1) }));

const stiffness = (layers, Lmm) => {
  const H = layers.reduce((s, l) => s + l.t, 0);
  let z = 0;
  const L = layers.map((l, idx) => {
    const c = z + l.t / 2 - H / 2;
    z += l.t;
    return { ...l, idx, a: c };
  });
  const b = 1000;
  let EI = 0;
  let outer = null;
  L.filter((l) => l.dir === "L").forEach((l) => {
    const A = b * l.t;
    let g = 1;
    if (Math.abs(l.a) > 1e-6) {
      const step = l.a > 0 ? -1 : 1; // transverse layer towards the centre
      const tT = L[l.idx + step]?.dir === "T" ? L[l.idx + step].t : 0;
      g = 1 / (1 + (Math.PI ** 2 * CL24.E * A * tT) / (CL24.Gr * b * Lmm * Lmm));
    }
    EI += CL24.E * ((b * l.t ** 3) / 12 + g * A * l.a ** 2);
    if (l.idx === 0) outer = { g, a: Math.abs(l.a), t: l.t, A };
  });
  return { EI, H, outer, b };
};

export const checkPanel = (p, comp) => {
  const c = CODES[p.code];
  const layers = parse(comp);
  const Lmm = p.L * 1000;
  const { EI, H, outer, b } = stiffness(layers, Lmm);
  const gk = p.G + RHO * (H / 1000); // kN/m on 1 m strip = N/mm
  const qk = p.Q;
  const qd = c.gG * gk + c.gQ * qk;
  const M = (qd * Lmm * Lmm) / 8;
  const V = (qd * Lmm) / 2;
  const sigma = (M * CL24.E * (outer.g * outer.a + outer.t / 2)) / EI;
  const tauR = (V * CL24.E * outer.g * outer.A * outer.a) / (EI * b);
  const fmd = (KMOD * CL24.fm) / c.gM;
  const frd = (KMOD * CL24.fr) / c.gM;
  const k = (5 * Lmm ** 4) / (384 * EI);
  const wInst = k * (gk + qk);
  const wFin = k * (gk * (1 + KDEF[p.sc]) + qk * (1 + p.psi2 * KDEF[p.sc]));
  return { bending: sigma / fmd, shear: tauR / frd, inst: wInst / (Lmm / 300), fin: wFin / (Lmm / 250), wInst, wFin, Q: qk };
};

export const sizePanel = (p) => {
  for (const [thick, layers, comp] of DATA.xlam) {
    const r = checkPanel(p, comp);
    if (Math.max(r.bending, r.shear, r.inst, r.fin) <= 1) return { thick, layers, comp, h: parseInt(thick, 10), ...r };
  }
  return null;
};
